import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Loader2,
  Copy,
  RefreshCw,
  Star,
  MessageCircle,
  Pencil,
  Save,
  Share2,
  Check,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Send,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import PricingModal from '../components/PricingModal';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const TONES = [
  { id: 'friendly', label: 'Friendly', desc: 'Warm & understanding', cost: 1 },
  { id: 'professional', label: 'Professional', desc: 'Business-like & clear', cost: 1 },
  { id: 'firm', label: 'Firm', desc: 'Assertive & direct', cost: 1 },
  { id: 'direct', label: 'Direct', desc: 'No fluff, just facts', cost: 1 },
  { id: 'savage', label: 'Savage', desc: 'Bold & quotable', cost: 2 },
];

const WSIS_STORAGE_KEY = 'sif_wsis_form';

export default function WhatShouldISayPage() {
  const { planInfo } = useAuth();

  // Restore saved form state
  const savedState = React.useMemo(() => {
    try {
      const saved = localStorage.getItem(WSIS_STORAGE_KEY);
      if (saved) return JSON.parse(saved);
    } catch {}
    return null;
  }, []);

  const [clientMessage, setClientMessage] = useState(savedState?.clientMessage || '');
  const [selectedTone, setSelectedTone] = useState(savedState?.selectedTone || 'professional');
  const [clientName, setClientName] = useState(savedState?.clientName || '');
  const [amountOwed, setAmountOwed] = useState(savedState?.amountOwed || '');
  const [serviceDesc, setServiceDesc] = useState(savedState?.serviceDesc || '');
  const [dueDate, setDueDate] = useState(savedState?.dueDate || '');
  const [showContext, setShowContext] = useState(savedState?.showContext || false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [usage, setUsage] = useState(null);
  const [editingIdx, setEditingIdx] = useState(null);
  const [editText, setEditText] = useState('');
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [savedIdx, setSavedIdx] = useState(null);
  const [showPricing, setShowPricing] = useState(false);
  const [pricingTrigger, setPricingTrigger] = useState('response_limit');
  const resultRef = useRef(null);

  const credits = planInfo?.credits || 0;
  const selectedToneCost = TONES.find(t => t.id === selectedTone)?.cost || 1;

  // Auto-save form state to localStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      localStorage.setItem(WSIS_STORAGE_KEY, JSON.stringify({
        clientMessage, selectedTone, clientName, amountOwed, serviceDesc, dueDate, showContext,
      }));
    }, 500);
    return () => clearTimeout(timer);
  }, [clientMessage, selectedTone, clientName, amountOwed, serviceDesc, dueDate, showContext]);

  useEffect(() => {
    fetchUsage();
  }, []);

  const fetchUsage = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/responses/usage`, { withCredentials: true });
      setUsage(res.data);
    } catch {
      // ignore
    }
  };

  const handleGenerate = async () => {
    if (!clientMessage.trim()) {
      toast.error('Paste the client message first');
      return;
    }

    setLoading(true);
    setResult(null);
    setEditingIdx(null);

    try {
      const payload = {
        client_message: clientMessage,
        tone: selectedTone,
      };
      if (clientName.trim()) payload.client_name = clientName;
      if (amountOwed) payload.amount_owed = parseFloat(amountOwed);
      if (serviceDesc.trim()) payload.service_description = serviceDesc;
      if (dueDate) payload.due_date = dueDate;

      const res = await axios.post(`${API_URL}/api/responses/generate`, payload, { withCredentials: true });
      setResult(res.data);
      localStorage.removeItem(WSIS_STORAGE_KEY);
      fetchUsage();
      setTimeout(() => {
        resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail.startsWith('upgrade_required')) {
        setPricingTrigger('no_credits');
        setShowPricing(true);
      } else {
        toast.error('Failed to generate responses. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text, idx) => {
    navigator.clipboard.writeText(text);
    setCopiedIdx(idx);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedIdx(null), 2000);
  };

  const handleEdit = (idx, text) => {
    setEditingIdx(idx);
    setEditText(text);
  };

  const handleSaveEdit = (idx) => {
    if (!result) return;
    const updated = { ...result };
    updated.responses = [...updated.responses];
    updated.responses[idx] = { ...updated.responses[idx], text: editText };
    setResult(updated);
    setEditingIdx(null);
    toast.success('Response updated');
  };

  const handleShareAsPost = (text) => {
    const shareText = `"${text}"\n\n-- Powered by SettledInFull | Get your money right.`;
    navigator.clipboard.writeText(shareText);
    toast.success('Copied as shareable post!');
  };

  const handleRegenerate = () => {
    handleGenerate();
  };

  const handleToneSelect = (toneId) => {
    setSelectedTone(toneId);
  };

  return (
    <div className="min-h-screen bg-[#050507] pt-24 pb-16 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-10 text-center" data-testid="wsis-header">
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF4500]">AI Response Generator</span>
          <h1 className="font-clash text-4xl sm:text-5xl font-medium text-white mt-2 mb-4" data-testid="wsis-title">
            What Should I Say?
          </h1>
          <p className="text-white/60 max-w-2xl mx-auto">
            Not sure what to say? Get the perfect reply in seconds.
          </p>
          <div className="mt-8 max-w-2xl mx-auto overflow-hidden rounded-xl">
            <img 
              src="https://customer-assets.emergentagent.com/job_debt-recovery-ai-2/artifacts/27p62r2w_pexels-vladislavsmigelski-12355752.jpg" 
              alt="Person texting on phone" 
              className="w-full h-48 sm:h-64 object-cover opacity-60"
              data-testid="wsis-hero-image"
            />
          </div>
        </div>

        {/* Credit Balance Badge */}
        <div className="flex justify-center mb-6" data-testid="wsis-credit-badge">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-white/60">
            <Zap className="w-3 h-3 text-amber-400" />
            {credits} credits remaining
            {credits === 0 && (
              <span className="text-[#FF4500] ml-1 font-medium cursor-pointer" onClick={() => { setPricingTrigger('no_credits'); setShowPricing(true); }}>- Get credits</span>
            )}
          </div>
        </div>

        {/* Input Section */}
        <div className="bg-[#0c0c10] border border-white/[0.06] rounded-2xl p-6 sm:p-8 mb-6" data-testid="wsis-input-section">
          <Label className="text-sm text-white/70 mb-2 block">What did your client say?</Label>
          <Textarea
            placeholder="Paste the message from your client here... e.g. 'Hey, I know I said I'd pay by Friday but something came up...'"
            value={clientMessage}
            onChange={(e) => setClientMessage(e.target.value)}
            className="bg-white/[0.03] border-white/[0.08] text-white placeholder:text-white/25 min-h-[120px] resize-none focus:border-[#FF4500]/40 focus:ring-[#FF4500]/10"
            data-testid="wsis-client-message-input"
          />

          {/* Tone Selector */}
          <div className="mt-6">
            <Label className="text-sm text-white/70 mb-3 block">Choose your tone</Label>
            <div className="flex flex-wrap gap-2" data-testid="wsis-tone-selector">
              {TONES.map((tone) => {
                const active = selectedTone === tone.id;
                const isSavage = tone.id === 'savage';
                return (
                  <button
                    key={tone.id}
                    onClick={() => handleToneSelect(tone.id)}
                    data-testid={`wsis-tone-${tone.id}`}
                    className={`relative flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all
                      ${active
                        ? 'bg-[#FF4500] text-white shadow-lg shadow-[#FF4500]/20'
                        : 'bg-white/[0.04] text-white/70 border border-white/[0.08] hover:bg-white/[0.08] hover:text-white'
                      }`}
                  >
                    {tone.label}
                    {isSavage && (
                      <span className="text-[10px] uppercase tracking-wider text-amber-400/80 font-semibold">+1</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Optional Context */}
          <div className="mt-5">
            <button
              onClick={() => setShowContext(!showContext)}
              className="flex items-center gap-2 text-sm text-white/40 hover:text-white/60 transition-colors"
              data-testid="wsis-toggle-context"
            >
              {showContext ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              Add context (optional)
            </button>

            {showContext && (
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 animate-in fade-in-0 slide-in-from-top-2 duration-300" data-testid="wsis-context-fields">
                <div>
                  <Label className="text-xs text-white/50 mb-1.5 block">Client Name</Label>
                  <Input
                    placeholder="e.g. Sarah"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    className="bg-white/[0.03] border-white/[0.08] text-white placeholder:text-white/25 focus:border-[#FF4500]/40"
                    data-testid="wsis-client-name"
                  />
                </div>
                <div>
                  <Label className="text-xs text-white/50 mb-1.5 block">Amount Owed</Label>
                  <Input
                    type="number"
                    placeholder="e.g. 500"
                    value={amountOwed}
                    onChange={(e) => setAmountOwed(e.target.value)}
                    className="bg-white/[0.03] border-white/[0.08] text-white placeholder:text-white/25 focus:border-[#FF4500]/40"
                    data-testid="wsis-amount-owed"
                  />
                </div>
                <div>
                  <Label className="text-xs text-white/50 mb-1.5 block">Service / Gig</Label>
                  <Input
                    placeholder="e.g. Wedding DJ set"
                    value={serviceDesc}
                    onChange={(e) => setServiceDesc(e.target.value)}
                    className="bg-white/[0.03] border-white/[0.08] text-white placeholder:text-white/25 focus:border-[#FF4500]/40"
                    data-testid="wsis-service-desc"
                  />
                </div>
                <div>
                  <Label className="text-xs text-white/50 mb-1.5 block">Due Date</Label>
                  <Input
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    className="bg-white/[0.03] border-white/[0.08] text-white placeholder:text-white/25 focus:border-[#FF4500]/40"
                    data-testid="wsis-due-date"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Generate Button */}
          <Button
            onClick={handleGenerate}
            disabled={loading || !clientMessage.trim()}
            className="mt-6 w-full bg-[#FF4500] hover:bg-[#CC3700] text-white h-12 rounded-xl text-base font-semibold shadow-lg shadow-[#FF4500]/20 transition-all hover:shadow-[#FF4500]/30 disabled:opacity-40"
            data-testid="wsis-generate-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Generating responses...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5 mr-2" />
                Generate Responses ({selectedToneCost} {selectedToneCost === 1 ? 'credit' : 'credits'})
              </>
            )}
          </Button>
        </div>

        {/* Results Section */}
        {result && (
          <div ref={resultRef} className="space-y-4 animate-in fade-in-0 slide-in-from-bottom-4 duration-500" data-testid="wsis-results">
            {/* Scenario Banner */}
            {result.scenario && (
              <div className="bg-[#0c0c10] border border-white/[0.06] rounded-xl px-5 py-3 flex items-center gap-3" data-testid="wsis-scenario">
                <Send className="w-4 h-4 text-[#FF4500] shrink-0" />
                <p className="text-sm text-white/60">
                  <span className="text-white/80 font-medium">Detected: </span>{result.scenario}
                </p>
              </div>
            )}

            {/* Response Cards */}
            {result.responses?.map((resp, idx) => (
              <ResponseCard
                key={idx}
                idx={idx}
                response={resp}
                tone={selectedTone}
                editingIdx={editingIdx}
                editText={editText}
                copiedIdx={copiedIdx}
                onCopy={handleCopy}
                onEdit={handleEdit}
                onSaveEdit={handleSaveEdit}
                onEditTextChange={setEditText}
                onCancelEdit={() => setEditingIdx(null)}
                onShare={handleShareAsPost}
              />
            ))}

            {/* Regenerate Button */}
            <div className="flex justify-center pt-2">
              <Button
                variant="ghost"
                onClick={handleRegenerate}
                disabled={loading}
                className="text-white/40 hover:text-white/70 gap-2"
                data-testid="wsis-regenerate-btn"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Regenerate with same settings
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Pricing Modal */}
      <PricingModal
        open={showPricing}
        onClose={() => setShowPricing(false)}
        trigger={pricingTrigger}
      />
    </div>
  );
}

function ResponseCard({
  idx,
  response,
  tone,
  editingIdx,
  editText,
  copiedIdx,
  onCopy,
  onEdit,
  onSaveEdit,
  onEditTextChange,
  onCancelEdit,
  onShare,
}) {
  const isBest = response.is_best;
  const isEditing = editingIdx === idx;
  const isCopied = copiedIdx === idx;

  return (
    <div
      data-testid={`wsis-response-${idx}`}
      className={`relative bg-[#0c0c10] border rounded-2xl overflow-hidden transition-all
        ${isBest
          ? 'border-[#FF4500]/40 shadow-lg shadow-[#FF4500]/5'
          : 'border-white/[0.06] hover:border-white/[0.12]'
        }`}
    >
      {/* Best Response Badge */}
      {isBest && (
        <div className="flex items-center gap-1.5 px-4 py-2 bg-[#FF4500]/10 border-b border-[#FF4500]/20" data-testid={`wsis-best-badge-${idx}`}>
          <Star className="w-3.5 h-3.5 text-[#FF4500] fill-[#FF4500]" />
          <span className="text-xs font-semibold text-[#FF4500] tracking-wide uppercase">Most Effective Response</span>
        </div>
      )}

      <div className="p-5">
        {/* Response Number */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs text-white/30 font-medium">Response {idx + 1}</span>
        </div>

        {/* Content */}
        {isEditing ? (
          <div className="space-y-3">
            <Textarea
              value={editText}
              onChange={(e) => onEditTextChange(e.target.value)}
              className="bg-white/[0.03] border-white/[0.08] text-white min-h-[100px] resize-none focus:border-[#FF4500]/40"
              data-testid={`wsis-edit-textarea-${idx}`}
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => onSaveEdit(idx)}
                className="bg-[#FF4500] hover:bg-[#CC3700] text-white text-xs h-8"
                data-testid={`wsis-save-edit-${idx}`}
              >
                <Check className="w-3 h-3 mr-1" />
                Save
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={onCancelEdit}
                className="text-white/50 hover:text-white/70 text-xs h-8"
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-white/80 text-[15px] leading-relaxed whitespace-pre-wrap">{response.text}</p>
        )}

        {/* Action Buttons */}
        {!isEditing && (
          <div className="flex items-center gap-1 mt-4 pt-3 border-t border-white/[0.04]">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onCopy(response.text, idx)}
              className="text-white/30 hover:text-white/70 h-8 px-3 gap-1.5 text-xs"
              data-testid={`wsis-copy-${idx}`}
            >
              {isCopied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
              {isCopied ? 'Copied' : 'Copy'}
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(idx, response.text)}
              className="text-white/30 hover:text-white/70 h-8 px-3 gap-1.5 text-xs"
              data-testid={`wsis-edit-${idx}`}
            >
              <Pencil className="w-3.5 h-3.5" />
              Edit
            </Button>

            {tone === 'savage' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onShare(response.text)}
                className="text-white/30 hover:text-[#FF4500]/70 h-8 px-3 gap-1.5 text-xs ml-auto"
                data-testid={`wsis-share-${idx}`}
              >
                <Share2 className="w-3.5 h-3.5" />
                Share as Post
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
